import { FC, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useCatalogData } from '@contexts/catalog-data';
import { SEFUrlsService } from '@services';
import { IFilterProp, IFiltersJSON, IOptionsProps } from '@types';
import CatalogCategory from './options/CatalogCategory';
import CoatingOptions from './options/CoatingOptions';
import ColorOptions from './options/ColorOptions';
import CountryOptions from './options/CountryOptions';
import DiameterOptions from './options/DiameterOptions';
import FlowerTypeOptions from './options/FlowerTypeOptions';
import HeightRangeOptions from './options/HeightRangeOptions';
import LengthOptions from './options/LengthOptions';
import ManufacturerOptions from './options/ManufacturerOptions';
import MaturityOptions from './options/MaturityOptions';
import PackingOptions from './options/PackingOptions';
import { PriceFilterOptions } from './options/PriceFilterOptions';
import QualityOptions from './options/QualityOptions';
import ShapeOptions from './options/ShapeOptions';
import SizeOptions from './options/SizeOptions';
import { StatusFilters } from './options/StatusFilters';
import VolumeOptions from './options/VolumeOptions';
import WeightOptions from './options/WeightOptions';
import WidthOptions from './options/WidthOptions';
import { filterStringToJson } from './utils';

import classes from './FiltersBar.module.scss';

interface IProps {
  locale?: string;
}

const FiltersBar: FC<IProps> = ({ locale }) => {
  const { catalog } = useCatalogData();
  const [selectedFilters, setSelectedFilters] = useState<IFiltersJSON>({});
  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();

  const { filters: filterOptionsData } = catalog || {};

  useEffect(() => {
    const filterParams = filterStringToJson(
      SEFUrlsService.getQueryFromUrl(pathName, searchParams.toString()),
      filterOptionsData
    );

    setSelectedFilters(filterParams);
  }, [searchParams, pathName]);

  if (!filterOptionsData) {
    return null;
  }

  const setFilters = (newFilters: IFilterProp[]) => {
    router.push(SEFUrlsService.getUrl(newFilters, selectedFilters));
  };

  const optionsProps: IOptionsProps = {
    filters: selectedFilters,
    setFilters,
    filterOptionsData,
    classes
  };

  const statusFilter = {
    type: 'status',
    content: <StatusFilters key="status-filter" {...optionsProps} label="Статус товарів" />
  };

  const categoryFilter = {
    type: 'category',
    content: <CatalogCategory key="category-filter" {...optionsProps} locale={locale} label="Види" />
  };

  const countryFilter = {
    type: 'country',
    content: <CountryOptions key="country-filter" {...optionsProps} label="Країна" />
  };

  const flowerTypeFilter = {
    type: 'flowerType',
    content: <FlowerTypeOptions key="flower-type-filter" {...optionsProps} label="Квітка" />
  };

  const manufacturerFilter = {
    type: 'manufacturer',
    content: <ManufacturerOptions key="manufacturer-filter" {...optionsProps} label="Виробник" />
  };

  const colorFilter = {
    type: 'color',
    content: <ColorOptions key="color-filter" {...optionsProps} label="Колір" />
  };

  const diameterFilter = {
    type: 'diameter',
    content: <DiameterOptions key="diameter-filter" {...optionsProps} label="Діаметр" />
  };

  const priceFilter = {
    type: 'price',
    content: <PriceFilterOptions key="price-filter" {...optionsProps} label="Ціна за шт, ₴" />
  };

  const heightFilter = {
    type: 'height',
    content: <HeightRangeOptions key="height-filter" {...optionsProps} label="Висота, см" />
  };

  const sizeFilter = {
    type: 'size',
    content: <SizeOptions key="size-filter" {...optionsProps} label="Розмір" />
  };

  const widthFilter = {
    type: 'width',
    content: <WidthOptions key="width-filter" {...optionsProps} label="Ширина" />
  };

  const coatingFilter = {
    type: 'coating',
    content: <CoatingOptions key="coating-filter" {...optionsProps} label="Тип покриття" />
  };

  const shapeFilter = {
    type: 'shape',
    content: <ShapeOptions key="shape-filter" {...optionsProps} label="Форма" />
  };

  const lengthFilter = {
    type: 'length',
    content: <LengthOptions key="length-filter" {...optionsProps} label="Довжина" />
  };

  const volumeFilter = {
    type: 'volume',
    content: <VolumeOptions key="volume-filter" {...optionsProps} label="Об'єм" />
  };

  const packingFilter = {
    type: 'packing',
    content: <PackingOptions key="packing-filter" {...optionsProps} label="Упаковка" />
  };

  const qualityFilter = {
    type: 'quality',
    content: <QualityOptions key="quality-filter" {...optionsProps} label="Якість" />
  };

  const maturityFilter = {
    type: 'maturity',
    content: <MaturityOptions key="maturity-filter" {...optionsProps} label="Зрілість" />
  };

  const weightFilter = {
    type: 'weight',
    content: <WeightOptions key="weight-filter" {...optionsProps} label="Вага" />
  };

  const filterElements = [
    statusFilter,
    categoryFilter,
    countryFilter,
    flowerTypeFilter,
    diameterFilter,
    colorFilter,
    manufacturerFilter,
    priceFilter,
    heightFilter,
    sizeFilter,
    widthFilter,
    coatingFilter,
    shapeFilter,
    lengthFilter,
    volumeFilter,
    packingFilter,
    qualityFilter,
    maturityFilter,
    weightFilter
  ];

  const sortedFilterElements =
    filterOptionsData?.filtersOrder?.length && filterElements.length
      ? filterOptionsData.filtersOrder.map((filter) => filterElements.find((el) => el.type === filter)).filter(Boolean)
      : filterElements;

  return (
    <aside className={classes.filtersSectionWrapper}>
      <div className={classes.filtersContainer}>{sortedFilterElements.map((element) => element?.content)}</div>
    </aside>
  );
};

export { FiltersBar };
